import { AfterViewInit, Component, inject, Input, OnDestroy } from '@angular/core';
import * as echarts from 'echarts';
import { Subject, Subscription, of } from 'rxjs';
import { takeUntil, switchMap } from 'rxjs/operators';
import { ConfigService } from '../../shared-services/config.service';
import { GlobalFilterService } from '../../shared-services/global-filter-service';
import { ChartFilterHandlerService } from '../../shared-services/chart-filter-handler.service';
import { ResponsiveLayoutService } from '../../shared-services/responsive-layout.service';
import { formatTooltipValue, getTooltipLabel, buildConfigurableAxisLabel } from '../../utils/chart.utils';
import { buildLegendSyncData, matchValue, MatchMode } from '../../utils/filter.utils';

@Component({
  selector: 'app-bubble-chart',
  imports: [],
  standalone: true,
  templateUrl: './bubble-chart.component.html',
  styleUrl: './bubble-chart.component.scss',
})
export class BubbleChartComponent implements AfterViewInit, OnDestroy {
  @Input() data!: any;
  protected myChart!: echarts.ECharts;
  protected responsiveLayoutService = inject(ResponsiveLayoutService);
  protected reflowSub?: Subscription;
  protected unSubscribe$: Subject<void> = new Subject();
  protected filterSub?: Subscription;
  protected allPointNames: string[] = [];
  protected selectedPointNames: string[] = [];

  constructor(
    protected configService: ConfigService,
    protected globalFilterService: GlobalFilterService,
    protected chartFilterHandler: ChartFilterHandlerService,
  ) { }

  ngAfterViewInit(): void {
    this.loadAndRenderChart()?.subscribe({
      next: (res: any) => {
        if (!res) return;
        const dataKey = this.data?.dataKey;
        const raw = dataKey ? res?.[dataKey] : (res?.data || res);
        const chartData = Array.isArray(raw) ? { points: raw } : raw;
        this.renderChart(chartData);
        this.addResizeListener();
      },
      error: () => {
        this.myChart.showLoading();
      },
    });

    this.filterSub = this.globalFilterService.filter$
      .pipe(
        takeUntil(this.unSubscribe$),
        switchMap((filters: any) => {
          if (!this.chartFilterHandler.shouldReload(filters, this.data?.widgetId)) {
            return of(null);
          }

          // Handle legendSync (visual dimming)
          if (filters?.legendSync) {
            this.handleExternalLegendSync(filters);
            if (filters.isCategoryFilter) {
              return this.loadAndRenderChart(filters) || of(null);
            }
            return of(null);
          }

          return this.loadAndRenderChart(filters) || of(null);
        }),
      )
      .subscribe({
        next: (res: any) => {
          if (!res) return;
          this.renderChart(res?.data);
          this.addResizeListener();
        },
        error: () => {
          this.myChart.showLoading();
        },
      });

    this.reflowSub = this.responsiveLayoutService.reflow$.subscribe(() => {
      this.myChart?.resize();
    });
  }

  protected loadAndRenderChart(filters?: any): any {
    const chartDom = document.getElementById(this.data.widgetId);
    if (!chartDom) return;

    if (!this.myChart) {
      this.myChart = echarts.init(chartDom);
    }

    this.myChart.showLoading();

    return this.configService.getConfig(this.data.apiUrl, this.data, filters);
  }

  protected renderChart(chartData?: { points: any[] }): void {
    // Extract point names (4th element = country/name) for cross-filtering
    this.allPointNames = (chartData?.points || []).map((p: any) => p[3] || '').filter(Boolean);

    // Bubble chart uses numeric (value) axes — secondary category axes don't apply.
    // Double-axis click handling is still supported via setupChartClickEvent.
    const xAxisOption: any = {
      scale: true,
      min: 0,
    };

    const yAxisOption: any = {
      scale: true,
      min: 60,
      ...(buildConfigurableAxisLabel(this.data?.chartConfig?.yAxis)
        ? { axisLabel: buildConfigurableAxisLabel(this.data?.chartConfig?.yAxis) }
        : {}),
    };

    const option = {
      tooltip: {
        trigger: 'item',
        appendToBody: true,
        formatter: (() => {
          const tooltipCfg = this.data?.chartConfig?.tooltip;
          if (tooltipCfg?.fields?.length) {
            return (params: any) => {
              const d = params.data;
              const lines: string[] = [];
              for (const field of tooltipCfg.fields) {
                const label = getTooltipLabel(field, tooltipCfg.autoCapitalizeKeys !== false);
                const idx = parseInt(field.key, 10);
                const rawValue = !isNaN(idx) ? d[idx] : d[field.key];
                lines.push(`<b>${label}:</b> ${formatTooltipValue(rawValue, field)}`);
              }
              return lines.join('<br/>');
            };
          }
          return (params: any) => {
            const [gdp, lifeExp, pop, country] = params.data;
            return `
            <strong>${country}</strong><br/>
            GDP: ${gdp}<br/>
            Life Expectancy: ${lifeExp}<br/>
            Population: ${pop}
          `;
          };
        })(),
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '3%',
        containLabel: true,
      },
      xAxis: xAxisOption,
      yAxis: yAxisOption,
      series: [
        {
          type: 'scatter',
          data: chartData?.points,
          symbolSize: function (data: any[]) {
            return Math.sqrt(data[2]) / 100;
          },
          emphasis: {
            focus: 'none',
            label: {
              show: true,
              formatter: (param: any) => param.data[3],
              position: 'top',
            },
          },
          itemStyle: { color: '#5470C6' },
        },
      ],
    };

    this.myChart.setOption(option, true);
    setTimeout(() => {
      this.myChart.hideLoading();
    }, 1000);

    this.setupChartClickEvent();
  }

  protected setupChartClickEvent(): void {
    this.myChart.off('click');
    this.myChart.on('click', (params: any) => {
      if (params.componentType === 'xAxis') {
        const axisIndex = params.xAxisIndex ?? params.axisIndex ?? 0;
        if (axisIndex === 1 && this.data?.enableDoubleXAxis && this.data?.enableXAxis2Click) {
          this.chartFilterHandler.handleDoubleXAxisClick(this.data, params.value);
        } else if (this.data?.enableLabelClick) {
          this.handlePointClick(params.value);
        }
      } else if (params.componentType === 'yAxis') {
        const axisIndex = params.yAxisIndex ?? 0;
        if (axisIndex === 1 && this.data?.enableDoubleYAxis && this.data?.enableYAxis2Click) {
          this.chartFilterHandler.handleDoubleYAxisClick(this.data, params.value);
        }
      } else {
        const raw = params.data?.value || params.data;
        const pointName = raw?.[3] || params.name;
        if (this.data?.enableLabelClick && pointName) {
          this.handlePointClick(pointName);
        } else {
          this.chartFilterHandler.updateFilterSelection(this.data, pointName);
        }
      }
    });
  }

  protected handlePointClick(pointName: string): void {
    if (!pointName) return;

    if (this.data?.isMultiSelect) {
      // Multiselect: toggle point in/out of selection
      const pos = this.selectedPointNames.indexOf(pointName);
      if (pos === -1) {
        this.selectedPointNames = [...this.selectedPointNames, pointName];
      } else {
        this.selectedPointNames = this.selectedPointNames.filter(n => n !== pointName);
      }
    } else {
      // Single-select: toggle same point off, or replace selection
      if (this.selectedPointNames.length === 1 && this.selectedPointNames[0] === pointName) {
        this.selectedPointNames = [];
      } else {
        this.selectedPointNames = [pointName];
      }
    }

    if (this.selectedPointNames.length === 0) {
      this.resetHighlight();
      this.broadcastLegendSync([]);
    } else {
      const matchMode = (this.data?.chartConfig?.categoryMatchMode || 'contains') as MatchMode;
      this.highlightMatchingPoints(this.selectedPointNames, matchMode);
      this.broadcastLegendSync(this.selectedPointNames);
    }
  }

  protected broadcastLegendSync(pointNames: string[]): void {
    const listenerConfig =
      this.data?.legendListener ||
      this.data?.labelListener ||
      this.data?.filterListener ||
      this.data?.filterListner;

    const legendKey = this.data?.legendKey || this.data?.legendFilterKey || this.data?.legendFilterkey || this.data?.filterKey;

    const filterData = buildLegendSyncData(
      [],
      pointNames,
      String(this.data?.widgetId || '').trim(),
      listenerConfig,
      { legendKey, isCategoryFilter: true },
    );
    filterData.selectedLegends = pointNames;

    // Include requestParams so listening charts send selected points in their API payload
    const requestParams: Record<string, any> = {};
    if (legendKey && pointNames.length > 0) {
      requestParams[legendKey] = pointNames;
    }
    (filterData as any).requestParams = requestParams;

    (this.globalFilterService as any).filterSubject.next(filterData);
  }

  protected handleExternalLegendSync(filters: any): void {
    if (String(filters.sourceWidgetId || '').trim() === String(this.data?.widgetId || '').trim()) return;
    if (!this.myChart) return;

    // Reset if empty selection — but preserve own active selection if any.
    if (filters.selectedLegends?.length === 0 && filters.selectedIndices?.length === 0) {
      if (this.selectedPointNames.length > 0) {
        const matchMode = (this.data?.chartConfig?.categoryMatchMode || 'contains') as MatchMode;
        this.highlightMatchingPoints(this.selectedPointNames, matchMode);
      } else {
        this.resetHighlight();
      }
      return;
    }

    if (filters.selectedLegends?.length > 0) {
      const matchMode = (this.data?.chartConfig?.categoryMatchMode || 'contains') as MatchMode;
      this.highlightMatchingPoints(filters.selectedLegends, matchMode);
    }
  }

  protected highlightMatchingPoints(legends: string[], matchMode: MatchMode): void {
    const option = this.myChart.getOption() as any;
    if (!option?.series) return;

    // Check if any points match before applying — preserve current state if no match
    const hasAnyMatch = option.series.some((s: any) =>
      s.data?.some((point: any) => {
        const name = (point?.value || point)?.[3] || point?.[3] || '';
        return legends.some((legend: string) => matchValue(name, legend, matchMode));
      })
    );
    if (!hasAnyMatch) return;

    const updatedSeries = option.series.map((s: any) => {
      if (!s.data) return s;
      const newData = s.data.map((point: any) => {
        const raw = point?.value || point;
        const name = raw?.[3] || '';
        const isMatch = legends.some((legend: string) => matchValue(name, legend, matchMode));
        return {
          value: raw,
          itemStyle: { opacity: isMatch ? 1 : 0.2 },
        };
      });
      return { ...s, data: newData };
    });

    this.myChart.setOption({ series: updatedSeries });
  }

  protected resetHighlight(): void {
    const option = this.myChart.getOption() as any;
    if (!option?.series) return;

    const updatedSeries = option.series.map((s: any) => {
      if (!s.data) return s;
      const newData = s.data.map((point: any) => {
        const raw = point?.value || point;
        return { value: raw, itemStyle: { opacity: 1 } };
      });
      return { ...s, data: newData };
    });

    this.myChart.setOption({ series: updatedSeries });
  }

  /**
   * Clears all local selections and resets visual highlights.
   */
  public reset(): void {
    this.selectedPointNames = [];
    this.resetHighlight();
  }

  protected addResizeListener(): void {
    window.addEventListener('resize', this.resizeChart);
  }
  protected resizeChart = () => {
    this.myChart?.resize();
  };

  ngOnDestroy(): void {
    this.reflowSub?.unsubscribe();
    this.filterSub?.unsubscribe();
    this.unSubscribe$.next();
    this.unSubscribe$.complete();
    window.removeEventListener('resize', this.resizeChart);
    this.myChart?.dispose();
  }
}
